import React from "react";
import formatDate from "../../../utils/formatDate";
import Button from "../../../components/Button/Button";
import {
  FaBell,
  FaCheckCircle,
  FaCoins,
  FaCreditCard,
  FaFileInvoice,
} from "react-icons/fa";
import { toAbsoluteArticlePhotoUrl } from "../../../services/articlesPhotoServices";

function hasEcheanceTag(message = "") {
  const clean = String(message)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return clean.includes("echeance");
}

function canGenerateInvoice(article) {
  const isBoutiqueArticle =
    article?.article_id !== null && article?.article_id !== undefined;

  return isBoutiqueArticle && article?.statut_reglement === "Réglé";
}

function getArticleName(article) {
  if (article?.article_id === null) {
    return "Règlement Licence-Cotisation";
  }

  return article?.Article?.nom || article?.nomArticle || "Nom non disponible";
}

const ArticleTable = ({
  articles,
  getStatusColor,
  defaultPhotoUrl,
  handlePayerClick,
  handlePayerEnPlusieursFoisClick,
  canUseInstallmentPayments = false,
  payingId,
  handleNotifyClick,
  notifyingId,
  isAlreadyNotified,
  handleGenerateInvoice,
}) => {
  const FALLBACK = "/img/no-image.jpg";

  return (
    <table className="hidden min-w-full divide-y divide-gray-200 bg-white font-lato sm:table">
      {articles.length > 0 ? (
        <tbody className="divide-y divide-gray-200 font-montserrat">
          {articles.map((article) => {
            const isDisabled = article.autorisation_achat === false;

            const canShowButtons = ["Non Réglé", "En Attente"].includes(
              article.statut_reglement,
            );

            const isInstallment = hasEcheanceTag(article.message_achat);

            const canShowInstallmentButton =
              canUseInstallmentPayments &&
              article.article_id === null &&
              !isInstallment;

            const rawPhoto =
              article?.Article?.photo_url ||
              article?.photoUrl ||
              defaultPhotoUrl;

            const imgSrc = rawPhoto
              ? toAbsoluteArticlePhotoUrl(rawPhoto)
              : FALLBACK;

            const articleName = getArticleName(article);

            const alreadyNotified = isAlreadyNotified?.(article.id);

            const showInvoice = canGenerateInvoice(article);

            return (
              <tr
                key={article.id}
                className="cursor-pointer transition hover:bg-slate-200 dark:bg-slate-200 dark:hover:bg-slate-400 dark:hover:text-slate-200"
              >
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500">
                  {formatDate(article.date_achat)}
                </td>

                <td className="px-4 py-2 text-sm text-gray-500">
                  <div className="font-semibold">
                    {article.quantite} x {articleName}
                  </div>

                  {article.message_achat && (
                    <div className="text-xs italic text-gray-800">
                      {article.message_achat}
                    </div>
                  )}

                  <span
                    className={`my-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(
                      article.statut_reglement,
                    )}`}
                  >
                    {article.statut_reglement}
                  </span>
                </td>

                <td className="px-4 py-2 text-sm text-gray-500">
                  <img
                    src={imgSrc}
                    alt={articleName}
                    className="aspect-square h-32 w-32 object-contain"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = FALLBACK;
                    }}
                  />
                </td>

                <td className="min-w-[90px] whitespace-nowrap px-4 py-2 text-xl font-bold text-gray-500">
                  {article.prix_total} €
                </td>

                <td className="text-center">
                  <div className="flex flex-wrap items-center justify-end gap-2 pr-2">
                    {canShowButtons && (
                      <>
                        {isDisabled && (
                          <span className="mr-2 text-xs italic text-red-700">
                            Paiement non activé
                          </span>
                        )}

                        <Button
                          text={
                            payingId === article.id ? "Redirection…" : "Payer"
                          }
                          icon={FaCreditCard}
                          onClick={
                            isDisabled
                              ? undefined
                              : () => handlePayerClick(article.id)
                          }
                          disabled={isDisabled || payingId === article.id}
                          className={`gap-2 ${
                            isDisabled ? "cursor-not-allowed opacity-40" : ""
                          }`}
                        />

                        {canShowInstallmentButton && (
                          <Button
                            text="Payer en plusieurs fois"
                            icon={FaCoins}
                            onClick={
                              isDisabled
                                ? undefined
                                : () =>
                                    handlePayerEnPlusieursFoisClick(article.id)
                            }
                            disabled={isDisabled}
                            className={`gap-2 whitespace-nowrap ${
                              isDisabled ? "cursor-not-allowed opacity-40" : ""
                            }`}
                          />
                        )}
                      </>
                    )}

                    {article.statut_reglement === "Réglé" && (
                      <>
                        {showInvoice && (
                          <Button
                            text="Facture"
                            icon={FaFileInvoice}
                            onClick={() => handleGenerateInvoice?.(article)}
                            className="gap-2"
                          />
                        )}

                        {alreadyNotified ? (
                          <span className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-2 text-xs font-semibold text-green-700">
                            <FaCheckCircle />
                            Admin prévenu
                          </span>
                        ) : (
                          <Button
                            text={
                              notifyingId === article.id
                                ? "Envoi…"
                                : "Prévenir l'admin"
                            }
                            icon={FaBell}
                            onClick={() => handleNotifyClick?.(article)}
                            disabled={notifyingId === article.id}
                            className="gap-2"
                          />
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      ) : (
        <tbody>
          <tr>
            <td colSpan="5" className="px-4 py-4 text-center dark:text-black">
              Pas d'articles commandés
            </td>
          </tr>
        </tbody>
      )}
    </table>
  );
};

export default ArticleTable;
